import React from 'react';
import dayjs from 'dayjs';
import {
  Box, Paper, TextField, MenuItem, Button, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination, Stack, ButtonGroup, Alert
} from '@mui/material';

import Client from '../Client';
import Config from '../ClientConfig';

const compactFieldSx = {
  minWidth: {
    xs: 'calc(50% - 6px)',
    sm: 150,
  },
  flex: {
    xs: '1 1 calc(50% - 6px)',
    sm: '0 0 auto',
  },
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};

const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};

const compactFilterPaperSx = {
  flexShrink: 0,
  p: {
    xs: 0.5,
    sm: 0.75,
  },
  mb: 0.75,
  borderRadius: '14px',
  overflow: 'hidden',
};

const compactTablePaperSx = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '14px',
};

const compactTableContainerSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  width: '100%',
};

const compactTableSx = {
  minWidth: {
    xs: 860,
    sm: 980,
    md: '100%',
  },
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: '#f8fafc',
    zIndex: 3,
  },
  '& .MuiTableCell-root': {
    py: { xs: 0.45, sm: 0.65 },
    px: { xs: 0.5, sm: 0.9 },
    fontSize: { xs: '0.72rem', sm: '0.84rem' },
    whiteSpace: 'nowrap',
  },
};

const compactPaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  minHeight: { xs: 38, sm: 50 },
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 38, sm: 50 },
    px: { xs: 0.25, sm: 1 },
    flexWrap: 'nowrap',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    m: 0,
  },
  '& .MuiIconButton-root': {
    p: { xs: 0.35, sm: 0.5 },
  },
};

class InventoryAdjustmentsAnalysis extends React.Component {
  constructor(props) {
    super(props);
    const thisMonth = dayjs().format('YYYY-MM');
    this.state = {
      client: new Client('none'),
      loading: false,
      applyErrorOpen: false,
      applyErrorDetail: '',
      locations: [],
      filters: { period: thisMonth, location: '', productQuery: '' },
      rows: [],
      all_rows: [],
      context: {},
      page: 0,
      rowsPerPage: -1,
    };
  }

  componentDidMount() { this.fetchLookups(); }

  nf(val, digits = 2) {
    if (val == null || val === '' || isNaN(val)) return '';
    const str = Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });
    return str;
  }

  fetchLookups() {
    const locationsResp = this.state.client.getData(Config.API_URL_LOCATIONS + 'list_locations/', { status: 1 }) || {};
    const locations = Array.isArray(locationsResp.locations) ? locationsResp.locations : [];
    locations.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    this.setState({ locations });
  }

  buildQuery() {
    const { period, location } = this.state.filters;
    const [year, month] = (period || '').split('-');
    return { year, month, location };
  }

  fetchData = () => {
    const { client, filters } = this.state;
    if (!filters.location) {
      this.openApplyError('Please select a location before applying.');
      return;
    }
    this.setState({ loading: true, page: 0 });

    const q = this.buildQuery();
    const resp = client.getData(Config.API_URL_REPORTS_TRANSACTIONS + 'inventory_adjustments_analysis/', q) || {};
    const merged = Array.isArray(resp.rows) ? resp.rows : [];
    const ctx = resp.context || {};

    let warningMsg = null;
    if (!ctx.hasMonthClose) {
      warningMsg = '⚠️ No Month Close found for this period/location.';
    } else {
      const missing = [];
      if (!ctx.hasInventoryCheck) missing.push('Inventory Check');
      if (!ctx.hasDecisions) missing.push('Month Decisions');
      if (missing.length) warningMsg = `⚠️ ${missing.join(' and ')} data missing for this period/location.`;
    }

    const filtered = this.applyProductFilter(merged, filters.productQuery);
    this.setState({ all_rows: merged, rows: filtered, loading: false, context: ctx, warningMsg });
  };

  setFilter = (name, value) => {
    this.setState(prev => {
      const filters = { ...prev.filters, [name]: value };
      let rows = prev.rows;
      if (name === 'productQuery') rows = this.applyProductFilter(prev.all_rows, value);
      return { filters, rows, page: 0 };
    });
  };

  applyProductFilter(rows, query) {
    const q = (query || '').trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter(r => String(r.productName || '').toLowerCase().includes(q));
  }

  exportCSV = () => {
    const { context, rows, filters } = this.state;
    // keep your existing rule
    if (!context.hasMonthClose || !context.hasInventoryCheck || !context.hasDecisions) return;

    // helpers: plain numeric strings, no thousands separators
    const num = (v, dp = 4) => {
      if (v == null || v === '' || isNaN(Number(v))) return '';
      let s = Number(v).toFixed(dp);
      if (dp > 0) { // trim trailing zeros
        while (s.includes('.') && s.endsWith('0')) s = s.slice(0, -1);
        if (s.endsWith('.')) s = s.slice(0, -1);
      }
      return s;
    };
    const money = (v) => num(v, 2);
    const pct = (v) => (v == null ? '' : num(v, 2)); // append % in CSV or leave as number; choose one
    const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`; // quote product to be safe

    const header = [
      'Product',
      'Check Qty',
      'Calc Qty (pre-adj)',
      'Diff',
      'Diff Value',
      'Adj. Qty',
      'Coga Qty',
      '% Adj. vs Coga'
    ];

    const lines = rows
      .filter(r => Number(r.inventoryCheckQty || 0) !== 0 || Number(r.calcQtyBeforeAdj || 0) !== 0)
      .map(r => [
        esc(r.productName),
        num(r.inventoryCheckQty, 4),
        num(r.calcQtyBeforeAdj, 4),
        num(r.diff, 4),
        money(r.diffValue),
        num(r.invDecisionQty, 4),   // your “Adj. Qty”
        num(r.cogaQty, 4),
        // choose numeric percentage (preferred for spreadsheets) OR a string with '%'
        // pct(r.pctDecisionVsCoga)            // numeric (easier to chart)
        (r.pctDecisionVsCoga == null ? '' : pct(r.pctDecisionVsCoga) + '%') // keep % sign
    ].join(','));

    // Add BOM for Excel friendliness
    const csv = ['\uFEFF' + header.join(','), ...lines].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const fname = `InventoryAdjustmentsAnalysis_${(filters.period || 'period')}.csv`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fname;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  openApplyError(detail) { this.setState({ applyErrorOpen: true, applyErrorDetail: detail }); }

  renderFilters() {
    const { filters, locations, loading, context, warningMsg } = this.state;
    const disableCSV = !context.hasMonthClose || !context.hasInventoryCheck || !context.hasDecisions;
    return (
      <Paper elevation={1} sx={compactFilterPaperSx}>
        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          alignItems="center"
          sx={{
            '& .MuiButton-root': compactButtonSx,
            '& .MuiButtonGroup-root .MuiButton-root': {
              height: 32,
              borderRadius: 0,
            },
            '& .MuiButtonGroup-root .MuiButton-root:first-of-type': {
              borderTopLeftRadius: '11px',
              borderBottomLeftRadius: '11px',
            },
            '& .MuiButtonGroup-root .MuiButton-root:last-of-type': {
              borderTopRightRadius: '11px',
              borderBottomRightRadius: '11px',
            },
          }}
        >
          <TextField
            type="month"
            label="Period"
            size="small"
            value={filters.period}
            onChange={e => this.setFilter('period', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={compactFieldSx}
          />

          <TextField
            select
            label="Location"
            size="small"
            value={filters.location}
            onChange={e => this.setFilter('location', e.target.value)}
            sx={compactFieldSx}
          >
            <MenuItem value="">All</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc.iref || loc.uid} value={loc.iref || loc.uid}>
                {loc.name}
              </MenuItem>
            ))}
          </TextField>

          <Box flexGrow={1} />

          <ButtonGroup size="small">
            <Button
              variant="contained"
              onClick={this.fetchData}
              disabled={loading}
            >
              Apply
            </Button>

            <Button onClick={this.exportCSV} disabled={disableCSV}>
              CSV
            </Button>

            <Button
              onClick={() =>
                this.setState({
                  filters: {
                    period: dayjs().format('YYYY-MM'),
                    location: '',
                    productQuery: '',
                  },
                  rows: this.state.all_rows,
                  page: 0,
                })
              }
            >
              Reset
            </Button>
          </ButtonGroup>

          {/* Product search AFTER Apply/CSV/Reset */}
          <TextField
            label="Product"
            placeholder="Search name"
            size="small"
            value={filters.productQuery}
            onChange={e => this.setFilter('productQuery', e.target.value)}
            sx={{
              ...compactFieldSx,
              flex: {
                xs: '1 1 100%',
                sm: '0 0 240px',
              },
              minWidth: {
                xs: '100%',
                sm: 220,
              },
            }}
          />
        </Stack>
        {warningMsg && (
          <Alert
            severity="warning"
            sx={{
              mt: 0.75,
              py: { xs: 0.25, sm: 0.5 },
              fontSize: { xs: '0.76rem', sm: '0.84rem' },
            }}
          >
            {warningMsg}
          </Alert>
        )}
      </Paper>
    );
  }

  renderTable() {
    const { rows, page, rowsPerPage, loading } = this.state;
    const paged = rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;
    return (
      <Paper elevation={1} sx={compactTablePaperSx}>
        <TableContainer sx={compactTableContainerSx}>
          <Table stickyHeader size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ fontWeight:'bold', bgcolor:'action.hover' }}>Product</TableCell>
                <TableCell align="right" sx={{ fontWeight:'bold', bgcolor:'action.hover' }}>Check Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight:'bold', bgcolor:'action.hover' }}>Calc Qty (pre-adj)</TableCell>
                <TableCell align="right" sx={{ fontWeight:'bold', bgcolor:'action.hover' }}>Diff</TableCell>
                <TableCell align="right" sx={{ fontWeight:'bold', bgcolor:'action.hover' }}>Diff Value</TableCell>
                <TableCell align="right" sx={{ fontWeight:'bold', bgcolor:'action.hover' }}>Adj. Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight:'bold', bgcolor:'action.hover' }}>Coga Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight:'bold', bgcolor:'action.hover' }}>% Adj. vs Coga</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((r,i) => (
                <TableRow key={i} hover>
                  <TableCell align="left">{r.productName}</TableCell>
                  <TableCell align="right">{this.nf(r.inventoryCheckQty,4)}</TableCell>
                  <TableCell align="right">{this.nf(r.calcQtyBeforeAdj,4)}</TableCell>
                  <TableCell align="right">{this.nf(r.diff,4)}</TableCell>
                  <TableCell align="right">{this.nf(r.diffValue,2)}</TableCell>
                  <TableCell align="right">{this.nf(r.invDecisionQty,4)}</TableCell>
                  <TableCell align="right">{this.nf(r.cogaQty,4)}</TableCell>
                  <TableCell align="right">{r.pctDecisionVsCoga==null?'':this.nf(r.pctDecisionVsCoga,2)+'%'}</TableCell>
                </TableRow>
              ))}
              {!paged.length && !loading && <TableRow><TableCell colSpan={8} align="center">No data</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination 
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(e,n)=>this.setState({page:n})}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e=>this.setState({rowsPerPage:Number(e.target.value),page:0})}
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={compactPaginationSx}
        />
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.6)',
              zIndex: 5,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  }

  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderFilters()}
        {this.renderTable()}
      </Box>
    );
  }
}

export default InventoryAdjustmentsAnalysis;